import { Box, Text } from '@user-portal/wikr-components';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import React, { useEffect } from 'react';

import { openModal } from 'store/modals/actions';

import {
  OfferTypes,
  cancelFlow_cancelOfferClick,
  cancelFlow_cancelOfferScreenLoad,
} from 'services/analytics/trackers/cancelFlow';

import useSpecialOfferModal from 'hooks/subscriptions/useSpecialOfferModal';
import { useCancelSubscription, useModals } from 'hooks';

import OutlinedButton from 'components/OutlinedButton';
import ModalHeader from 'components/ModalHeader/ModalHeader';
import Modal from 'components/Modal';
import DiscountOffer from 'components/DiscountOffer';

import * as S from './styled';

import { ModalName } from 'types/modals';

const SpecialOfferModalContainer = ({ onError }: { onError?: () => void } = {}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { closeModal } = useModals();
  const { cancellationCandidate, onCancelSubscription, discardCancellation } = useCancelSubscription();

  const { discountSubscription, candidatePriceData, discountPriceData, onUpdateSubscription } = useSpecialOfferModal(
    cancellationCandidate!.external_id
  );

  if (!cancellationCandidate || !discountSubscription) {
    onError?.();

    return null;
  }

  useEffect(() => {
    cancelFlow_cancelOfferScreenLoad({
      offer_type: OfferTypes.Discount_offer,
      subscription_id: cancellationCandidate.external_id,
    });
  }, []);

  const handleCancel = () => {
    onCancelSubscription();

    cancelFlow_cancelOfferClick({
      action: 'cancel',
      subscription_id: cancellationCandidate.external_id,
      offer_type: OfferTypes.Discount_offer,
    });
  };

  const handleClose = () => {
    closeModal();
    discardCancellation();

    cancelFlow_cancelOfferClick({
      action: 'close',
      subscription_id: cancellationCandidate.external_id,
      offer_type: OfferTypes.Discount_offer,
    });
  };

  const handleUpdate = () => {
    onUpdateSubscription();

    cancelFlow_cancelOfferClick({
      action: 'claim_offer',
      subscription_id: cancellationCandidate.external_id,
      offer_type: OfferTypes.Discount_offer,
    });
  };

  const goBack = () => {
    dispatch(openModal(ModalName.REASON_MODAL));
  };

  return (
    <Modal isOpen fullscreen>
      <>
        <ModalHeader onBack={goBack} onClose={handleClose} />
        <S.ModalContainer data-locator="discountOfferContainer">
          <Box $paddingX={25}>
            <Text type="h4" medium center mb={12}>
              {t('subscription.cancellation.specialOffer.withDynamicPercent.title', { percent: 50 })}
            </Text>
            <Text type="large-text" center mb={24}>
              {t('subscription.cancellation.specialOffer.withDynamicPercent.subtitle')}
            </Text>
          </Box>
          <DiscountOffer
            mb={24}
            badgeBackgroundColor="text-secondary"
            badgeText="subscription.cancellation.specialOffer.firstBadge"
            price={candidatePriceData!.price}
            period={candidatePriceData!.period}
            currencySign={candidatePriceData!.currency}
          />
          <DiscountOffer
            mb={24}
            badgeBackgroundColor="critical-default"
            badgeText="subscription.cancellation.specialOffer.withDynamicPercent.secondBadge"
            badgePercent={50}
            isNewOffer
            oldPrice={candidatePriceData!.price}
            price={discountPriceData!.price}
            period={discountPriceData!.period}
            currencySign={discountPriceData!.currency}
            onSubmitDiscount={handleUpdate}
          />
          <OutlinedButton dataLocator="discountOfferCancelSubscriptionButton" onClick={handleCancel}>
            {t('subscription.text.cancel')}
          </OutlinedButton>
        </S.ModalContainer>
      </>
    </Modal>
  );
};

export default SpecialOfferModalContainer;
